import {
  DataTable,
  Paginator,
  SkeletonLoader,
  Button,
  FilterBar,
  PageActions,
  ExportButton,
  StatusPill,
  StatusTabs,
  naBody,
} from 'components';

import { humanReadable, humanReadableGermanDate } from 'utils';
import { locales, ROWS_PER_PAGE_OPTIONS } from 'constants/index';

import columns from './columns';
import {
  ListOneDeleteOpenPendingTicketConfirmationDialog,
  ListOneFixAndPromoteDialog,
  ListOneDocumentsDialog,
  ListOneTicketDrawer,
} from './components';
import { ListOneUiProps, ListOneTicket } from './ListOne.types';

import './list-one.scss';

function TypeBodyTemplate(row: ListOneTicket) {
  return (
    <span
      className={`colored-circle colored-circle--${row.type.toLowerCase()}`}
    >
      {row.type}
    </span>
  );
}

function dateBodyTemplate(field: string) {
  return function (row: ListOneTicket) {
    if (row[field as keyof ListOneTicket])
      return humanReadable(row[field as keyof ListOneTicket] as string);

    return 'N/A';
  };
}

function affectedDateTemplate(row: ListOneTicket) {
  if (row.affectedDate) return row.affectedDate;

  return 'N/A';
}

function StatusBodyTemplate(row: ListOneTicket) {
  return <StatusPill status={row.status} />;
}

// A click on a row opens it, unless it landed on something in the row that
// does its own thing: a button or a link.
function isInteractive(target: EventTarget | null) {
  return target instanceof Element && !!target.closest('button, a, input');
}

const FIXABLE = (ticket: ListOneTicket) =>
  FIXABLE_STATUS_IDS.includes(ticket.statusId);

const FIXABLE_STATUS_IDS = [3, 13, 14, 51];

interface RowActions {
  canDelete: boolean;
  onDelete: (ticketId: string) => VoidFunction;
  onFix: (ticket: ListOneTicket) => void;
  onDocuments: (ticket: ListOneTicket) => void;
  onOpen: (ticket: ListOneTicket) => void;
}

/*
 * Fix where the status can be fixed, Documents (or Approved) where the
 * country needs approval paperwork, Delete for an open addition when the user
 * may, and View on every row.
 */
function ActionsBodyTemplate(actions: RowActions) {
  return (row: ListOneTicket) => (
    <div className="az-tickets__row-actions">
      {/* First, so it lines up down the column whatever follows it. */}
      <button
        type="button"
        className="az-tickets__view"
        onClick={() => actions.onOpen(row)}
      >
        View
      </button>

      {FIXABLE_STATUS_IDS.includes(row.statusId) && (
        <Button
          className="az-tickets__row-button"
          onClick={() => actions.onFix(row)}
          label="Fix"
          outlined
        />
      )}

      {row.approval?.required &&
        (row.approval.approvedAt ? (
          <StatusPill status="Approved" tone="success" />
        ) : (
          <Button
            className="az-tickets__row-button"
            onClick={() => actions.onDocuments(row)}
            label="Documents"
            outlined
          />
        ))}

      {actions.canDelete && (
        <button
          type="button"
          className="az-tickets__row-delete"
          onClick={actions.onDelete(row._id)}
        >
          Delete
        </button>
      )}

    </div>
  );
}

export default function ListOneUi({
  isLoading,
  isExporting,
  isDeleteOpenPendingTicketButtonVisible,
  isFixAndPromoteDialogVisible,
  fixAndPromoteTicket,
  isFixAndPromoteLoading,
  documentsTicketId,
  openPendingTicketId,
  totalRecords,
  statusCounts,
  options,
  filter,
  data,
  handlers,
}: ListOneUiProps) {
  const getMappedColumns = (column: any) => {
    if (column.field === 'type') {
      return {
        ...column,
        body: TypeBodyTemplate,
      };
    }

    if (column.field === 'createdDate' || column.field === 'updatedDate') {
      return {
        ...column,
        body: dateBodyTemplate(column.field),
      };
    }

    if (column.field === 'affectedDate')
      return {
        ...column,
        body: affectedDateTemplate,
      };

    if (column.field === 'status') {
      return {
        ...column,
        body: StatusBodyTemplate,
      };
    }

    if (column.field === 'actions') {
      return {
        ...column,
        body: ActionsBodyTemplate({
          // Deleting is offered on open additions only, as before.
          canDelete:
            isDeleteOpenPendingTicketButtonVisible &&
            filter.ticketType === 'AD' &&
            (filter.ticketStatus === 'open-pending' ||
              filter.ticketStatus === 'open-error'),
          onDelete:
            handlers.handleDeleteOpenPendingTicketConfirmationDialogVisible,
          onFix: handlers.handleFixAndPromoteClick,
          onDocuments: handlers.handleDocumentsClick,
          onOpen: handlers.handleTicketOpen,
        }),
      };
    }

    return {
      ...column,
      body: naBody(column.field),
    };
  };

  return (
    <div className="az-tickets">
      <PageActions>
        <ExportButton
          disabled={isLoading || !data.tickets || data.tickets.length === 0}
          onClick={handlers.handleDownloadClick}
          loading={isExporting}
        />
      </PageActions>

      <StatusTabs
        label="Ticket status"
        active={filter.ticketStatus || 'All'}
        onChange={handlers.handleStatusTabChange}
        tabs={[
          { key: 'All', label: 'All tickets', count: statusCounts.All },
          {
            key: 'open-pending',
            label: 'Pending',
            count: statusCounts['open-pending'],
          },
          {
            key: 'open-error',
            label: 'Errors',
            count: statusCounts['open-error'],
          },
        ]}
      />

      <FilterBar
        loading={isLoading}
        onReset={handlers.handleFilterClear}
        search={{
          inputs: [
            {
              name: 'employeeId',
              placeholder: locales.general.employeeID,
              value: filter.employeeId || '',
              fieldWidth: 12,
            },
            {
              name: 'personId',
              placeholder: locales.general.personID,
              value: filter.personId || '',
              fieldWidth: 12,
            },
          ],
          onChange: handlers.handleSearchChange,
          onSubmit: handlers.handleSearchClick,
          width: 5,
        }}
        filters={[
          {
            kind: 'dropdown',
            name: 'status',
            placeholder: locales.general.filterByStatus,
            fieldWidth: 18,
            value: filter.ticketStatus || '',
            options: options.ticketStatuses,
            onChange: handlers.handleTicketStatusChange,
            width: 3,
          },
          {
            kind: 'dropdown',
            name: 'ticketType',
            placeholder: locales.general.filterByType,
            fieldWidth: 14,
            value: filter.ticketType || '',
            options: options.ticketTypes,
            onChange: handlers.handleTicketTypeChange,
            width: 3,
          },
        ]}
        advanced={[
          {
            kind: 'dropdown',
            name: 'country',
            label: 'Country',
            placeholder: locales.general.filterByCountry,
            fieldWidth: 18,
            value: filter.country || '',
            options: options.countries,
            onChange: handlers.handleCountryChange,
          },
          {
            kind: 'dropdown',
            name: 'statusCode',
            label: 'Status code',
            placeholder: locales.general.filterByStatusCode,
            value: filter.statusCodes || '',
            options: options.statusCodes,
            onChange: handlers.handleStatusCodeChange,
          },
          {
            kind: 'date',
            name: 'dates',
            label: 'Date range',
            value: filter.dates,
            fieldWidth: 24,
            onChange: handlers.handleDateChange,
          },
        ]}
      />

      {isLoading ? (
        <SkeletonLoader height="10rem" />
      ) : (
        <DataTable
          onRowClick={(event) => {
            if (!isInteractive(event.originalEvent.target)) {
              handlers.handleTicketOpen(event.data as ListOneTicket);
            }
          }}
          rowClassName={(row: ListOneTicket) => ({
            'az-tickets__row': true,
            'az-tickets__row--open': row._id === data.drawerTicket?._id,
          })}
          sortMode="multiple"
          dataKey="id"
          totalRecords={totalRecords}
          value={data.tickets.map((ticket) => ({
            ...ticket,
            affectedDate: humanReadableGermanDate(
              ticket.affectedDate as string,
              'DD/MM/YYYY'
            ),
          }))}
          columns={columns.map(getMappedColumns)}
        />
      )}

      {data.tickets?.length > 0 &&
        (isLoading ? (
          <SkeletonLoader height="3rem" width="40rem" />
        ) : (
          <Paginator
            first={filter.first}
            rows={filter.rows}
            totalRecords={totalRecords}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            onPageChange={handlers.handlePageChange}
          />
        ))}

      <ListOneDeleteOpenPendingTicketConfirmationDialog
        visible={openPendingTicketId.length > 0}
        ticketId={openPendingTicketId}
        onHide={handlers.handleDeleteOpenPendingTicketConfirmationDialogHide}
        footer={
          <>
            <Button
              onClick={
                handlers.handleDeleteOpenPendingTicketConfirmationDialogHide
              }
              outlined
              label={locales.general.close}
            />

            <Button
              onClick={handlers.handleDeleteOpenPendingTicketClick}
              label={'Confirm'}
            />
          </>
        }
      />

      <ListOneFixAndPromoteDialog
        visible={isFixAndPromoteDialogVisible}
        ticket={fixAndPromoteTicket}
        onHide={handlers.handleFixAndPromoteDialogHide}
        onSubmit={handlers.handleFixAndPromoteSubmit}
        isLoading={isFixAndPromoteLoading}
      />

      <ListOneTicketDrawer
        ticket={data.drawerTicket}
        onHide={handlers.handleTicketDrawerHide}
        onReviewDocuments={handlers.handleDocumentsClick}
        onFix={handlers.handleFixAndPromoteClick}
        canFix={FIXABLE}
      />

      <ListOneDocumentsDialog
        visible={documentsTicketId.length > 0}
        ticketId={documentsTicketId}
        onHide={handlers.handleDocumentsDialogHide}
        onApproved={handlers.handleDocumentsApproved}
      />
    </div>
  );
}
